import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import straivLogo from '@/assets/straiv-logo-share.png';
import { ShareableWorkout } from '../ShareWorkoutCard';
import { RunningRouteVector, RouteCoordinate } from './RunningRouteVector';

interface ShareTemplateProps {
  workout: ShareableWorkout;
}

// Exact format for share image - no rounding, 1 decimal only when fractional
const formatTimeExact = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const secRounded = Math.round(s);
  const secStr = Math.abs(s - secRounded) < 0.01 ? String(secRounded).padStart(2, '0') : s.toFixed(1);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${secStr}`;
  return `${m}:${secStr}`;
};
// Pace: always whole seconds (0:37 not 0:37.5)
const formatPaceExact = (paceSeconds: number) => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return '--:--';
  const m = Math.floor(paceSeconds / 60);
  const s = Math.round(paceSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Add proper spacing to workout names: "ChestDay" -> "Chest Day", "UPPER_BODY" -> "Upper Body"
const formatShareTitle = (title: string) => {
  if (!title?.trim()) return title;
  const s = title
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return s.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

export const ShareTemplateMinimal = forwardRef<HTMLDivElement, ShareTemplateProps>(
  ({ workout }, ref) => {
    const { t } = useTranslation();
    const isRunning = workout.workout_type?.toLowerCase() === 'running';

    const formatTime = (minutes: number) => {
      const h = Math.floor(minutes / 60);
      const m = Math.floor(minutes % 60);
      const s = Math.round((minutes % 1) * 60);
      if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      return `${m}:${String(s).padStart(2, '0')}`;
    };
    
    // Get route coordinates if available (will be populated once native iOS passes data)
    const routeCoordinates: RouteCoordinate[] = (workout as any).route_coordinates || [];

    const secondaryLabelStyle: React.CSSProperties = {
      fontSize: '28px',
      lineHeight: 1,
      color: 'rgba(255,255,255,0.5)',
      marginBottom: '-12px',
    };

    const secondaryValueStyle: React.CSSProperties = {
      fontSize: '64px',
      color: '#0AFFC2',
      lineHeight: 1.05,
    };

    return (
      <div
        ref={ref}
        className="relative overflow-hidden flex flex-col items-center"
        style={{
          width: '1080px',
          height: '1980px',
          background: 'transparent',
          padding: 0,
        }}
      >
        {/* Content block */}
        <div
          className="flex flex-col items-center"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '320px',
            padding: '0 80px',
          }}
        >
          {/* Logo */}
          <img src={straivLogo} alt="STRAIV" style={{ width: '400px', height: 'auto' }} />

          {/* Pull title closer to the logo (logo asset has internal whitespace) */}
          <div style={{ height: '0px' }} />

          {/* Workout Title */}
          <div
            className="font-bold text-center"
            style={{
              fontSize: '52px',
              color: '#0AFFC2',
              lineHeight: 1.15,
              marginTop: '-56px',
              marginBottom: '40px',
            }}
          >
            {formatShareTitle(workout.title)}
          </div>

          {/* Running route + bottom stats */}
          {isRunning && routeCoordinates.length >= 2 && (
            <div className="flex flex-col items-center" style={{ marginBottom: '32px' }}>
              <RunningRouteVector
                coordinates={routeCoordinates}
                width={600}
                height={420}
                strokeColor="#0AFFC2"
                strokeWidth={3}
                showStartEnd={false}
              />
              <div className="grid grid-cols-4 gap-4 text-center w-full" style={{ marginTop: '40px' }}>
                <div>
                  <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>{t('components.shareTemplates.distance')}</p>
                  <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2', lineHeight: 1.2 }}>{workout.distance_km || '0'}</p>
                </div>
                <div>
                  <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>{t('components.shareTemplates.pace')}</p>
                  <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2', lineHeight: 1.2 }}>
                    {(workout as any).pace_seconds_per_km != null ? formatPaceExact((workout as any).pace_seconds_per_km) : (workout.average_pace || '--:--')}
                  </p>
                </div>
                <div>
                  <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>{t('components.shareTemplates.time')}</p>
                  <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2', lineHeight: 1.2 }}>
                    {(workout as any).duration_seconds != null ? formatTimeExact((workout as any).duration_seconds) : formatTime(workout.duration_minutes)}
                  </p>
                </div>
                <div>
                  <p className="uppercase tracking-wider" style={{ fontSize: '24px', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>{t('components.shareTemplates.calories')}</p>
                  <p className="font-bold" style={{ fontSize: '44px', color: '#0AFFC2', lineHeight: 1.2 }}>{workout.calories_burned || '--'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Non-route running fallback or gym stats */}
          {isRunning && routeCoordinates.length < 2 && (
            <>
              <p className="font-medium uppercase tracking-wider" style={{ fontSize: '28px', lineHeight: 1, color: 'rgba(255,255,255,0.5)', marginBottom: '-28px' }}>{t('components.shareTemplates.distance')}</p>
              <div className="flex items-baseline justify-center" style={{ gap: '16px', marginTop: '-12px', marginBottom: '56px' }}>
                <span className="font-bold" style={{ fontSize: '140px', color: '#0AFFC2', lineHeight: 1 }}>{workout.distance_km || '0'}</span>
                <span className="font-medium" style={{ fontSize: '56px', color: '#0AFFC2' }}>km</span>
              </div>
              <div className="flex flex-wrap justify-center" style={{ gap: '80px' }}>
                <div className="text-center">
                  <p style={secondaryLabelStyle}>{t('components.shareTemplates.pace')}</p>
                  <p className="font-bold" style={secondaryValueStyle}>
                    {(workout as any).pace_seconds_per_km != null ? formatPaceExact((workout as any).pace_seconds_per_km) : (workout.average_pace || '--:--')}
                  </p>
                </div>
                <div className="text-center">
                  <p style={secondaryLabelStyle}>{t('components.shareTemplates.time')}</p>
                  <p className="font-bold" style={secondaryValueStyle}>
                    {(workout as any).duration_seconds != null ? formatTimeExact((workout as any).duration_seconds) : formatTime(workout.duration_minutes)}
                  </p>
                </div>
                <div className="text-center">
                  <p style={secondaryLabelStyle}>{t('components.shareTemplates.calories')}</p>
                  <p className="font-bold" style={secondaryValueStyle}>{workout.calories_burned || '--'}</p>
                </div>
              </div>
            </>
          )}

          {/* Volume label */}
          {!isRunning && (
          <p
            className="font-medium uppercase tracking-wider"
            style={{
              fontSize: '28px',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.5)',
              marginBottom: '-28px',
            }}
          >
            {t('components.shareTemplates.volume')}
          </p>
          )}

          {/* Main stat (gym only) */}
          {!isRunning && (
          <div
            className="flex items-baseline justify-center"
            style={{ gap: '16px', marginTop: '-12px', marginBottom: '56px' }}
          >
            <span className="font-bold" style={{ fontSize: '140px', color: '#0AFFC2', lineHeight: 1 }}>
              {workout.total_volume ? Math.round(workout.total_volume) : 0}
            </span>
            <span className="font-medium" style={{ fontSize: '56px', color: '#0AFFC2' }}>
              kg
            </span>
          </div>
          )}

          {/* Secondary stats row (gym only) */}
          <div className="flex" style={{ gap: '80px' }}>
            {!isRunning && (
              <>
                <div className="text-center">
                  <p style={secondaryLabelStyle}>{t('components.shareTemplates.sets')}</p>
                  <p className="font-bold" style={secondaryValueStyle}>
                    {workout.total_sets || 0}
                  </p>
                </div>
                <div className="text-center">
                  <p style={secondaryLabelStyle}>{t('components.shareTemplates.time')}</p>
                  <p className="font-bold" style={secondaryValueStyle}>
                    {formatTime(workout.duration_minutes)}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Footer - right under stats */}
          <p
            className="tracking-wide text-center"
            style={{ fontSize: '28px', color: 'rgba(255,255,255,0.4)', marginTop: '48px' }}
          >
            {t('components.shareTemplatesExtra.poweredBy')}
          </p>
        </div>
      </div>
    );
  }
);

ShareTemplateMinimal.displayName = 'ShareTemplateMinimal';
